import React, { Component } from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import cx from 'classnames';
import { get } from 'lodash';

import { Button } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { FormFieldSets } from '../../../lib/mighty-fields';
import { SessionStoreClass } from '../../../stores';
import { FormManager } from '../../../utils';
import { Form } from '../../common-formsy';

const { PERMISSIONS } = AppConstants;

const FIELD_SETS = [
  [{
    field: 'body',
    label: null,
    placeholder: 'Add account note...',
    required: true,
    type: 'textDynamic',
  }],
];

interface IProps {
  loanAccountId: string;
  // The note only appears once the feed has been re-read
  onNoteCreated: () => Promise<void>;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

/**
 * The note composer at the top of the feed, following the case page's NewCaseNote minus its
 * mentions: notifying a colleague is a case feature, and nothing on a loan account is tagged.
 */
@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class NewNote extends Component<IProps> {
  @observable private isFocused = false;

  private formManager: FormManager;

  public constructor (props: IProps) {
    super(props);

    this.formManager = new FormManager({
      fieldSets: FIELD_SETS,
      onSave: this.onSave,
      onSuccess: this.onSuccess,
    });
  }

  private get injected () {
    return this.props as IInjected;
  }

  /**
   * A note being written takes the taller box and the footer under it; until then the composer is
   * the one line the case page's is. c.f. NewCaseNote, where the same two conditions expand it.
   */
  private get isExpanded () {
    return this.isFocused || !!get(this.formManager.submitData, 'body', '').trim();
  }

  private onFocus () {
    this.isFocused = true;
  }

  private onBlur (event: React.FocusEvent<HTMLDivElement>) {
    // Clicking Save blurs the textarea before the click lands on it, so focus moving to something
    // else in the composer leaves it expanded rather than pulling the button out from under it.
    if (event.currentTarget.contains(event.relatedTarget as Node)) { return; }

    this.isFocused = false;
  }

  private async onSave (formData: object) {
    const { body } = formData as { body: string }
      , { Clients } = this.injected
      ;

    await Clients.loanAccountNotes.create({ account: this.props.loanAccountId, body });
  }

  private async onSuccess () {
    this.formManager.reset();

    await this.props.onNoteCreated();
  }

  public render () {
    const { SessionStore } = this.injected;

    // The endpoint rejects a note from a user without the add permission, so only offer the composer
    // to users who have it -- as the lending tabs do with their own add buttons.
    if (!SessionStore.userHasPermission(PERMISSIONS.ADD_LOAN_ACCOUNT_NOTE)) { return null; }

    return (
      <Form {...this.formManager.formProps}>
        <div className={cx('new-note', { expanded: this.isExpanded })} onBlur={this.onBlur} onFocus={this.onFocus}>
          <FormFieldSets fieldSets={FIELD_SETS} {...this.formManager.formGroupsProps} />

          {this.isExpanded &&
            <div className='form-footer'>
              <Button
                bsSize='small'
                bsStyle='primary'
                className='btn-save-note'
                disabled={this.formManager.submitDisabled()}
                type='submit'
              >
                {this.formManager.isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          }
        </div>
      </Form>
    );
  }
}

export default NewNote;
